import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Loader2, AlertCircle, Users, Shield, Target, MessageSquare, BookOpen, BarChart3, Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const offerings = [
  {
    Icon: Users,
    title: 'Team Workshops',
    duration: 'Half-day or full-day',
    desc: 'Facilitated sessions that give your team a shared language for navigating cultural difference. Each participant completes the CFQ with informed consent and leaves with a personal development goal.',
  },
  {
    Icon: Target,
    title: 'Executive Retreats',
    duration: '1–2 days',
    desc: 'Deeper immersion for senior leaders. Explores how cultural fluency shapes decision-making, trust-building, and team dynamics at the top.',
  },
  {
    Icon: MessageSquare,
    title: 'Keynote Speaking',
    duration: '45–90 minutes',
    desc: 'A compelling introduction to cultural fluency for conferences, leadership summits, and away days. Tailored to your audience and sector.',
  },
  {
    Icon: BookOpen,
    title: 'Ongoing Advisory',
    duration: 'Quarterly',
    desc: 'Sustained support for organisations embedding cultural fluency into policy, hiring, onboarding, and team culture.',
  },
];

const principles = [
  { Icon: Shield, title: 'Consent-based', desc: 'Individual CFQ results are private. Sharing with the team is always voluntary — never mandated by management.' },
  { Icon: Users, title: 'Facilitated, not automated', desc: 'Every session is led by a trained practitioner. The CFQ is a conversation starter, not a self-service survey.' },
  { Icon: Target, title: 'Practical outcomes', desc: 'Each participant leaves with one observable development goal. No vague action plans — a specific behaviour to work on.' },
  { Icon: BarChart3, title: 'No cohort scoring', desc: 'We do not aggregate or compare individual results. The assessment is developmental, not evaluative.' },
];

const Organisations = () => {
  const [form, setForm] = useState({ name: '', email: '', organisation: '', role: '', team_size: '', message: '' });
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');
    try {
      const payload = {
        name: form.name,
        email: form.email,
        subject: `Organisation Enquiry — ${form.organisation}`,
        message: `Organisation: ${form.organisation}\nRole: ${form.role || 'Not specified'}${form.team_size ? `\nTeam size: ${form.team_size}` : ''}\n\n${form.message}`,
      };
      const res = await fetch(`${API_URL}/api/email/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to send');
      }
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message);
    }
  };

  const inputClass = "w-full bg-white/[0.03] border border-cream/[0.08] px-5 py-4 text-base text-cream placeholder-cream/15 font-light tracking-wide focus:outline-none focus:border-gold/30 transition-all disabled:opacity-50";

  return (
    <div className="bg-navy min-h-screen" data-testid="organisations-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <span className="block text-[11px] tracking-[0.3em] uppercase text-gold/60 font-sans font-medium mb-6">For Organisations</span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-cream font-light tracking-tight leading-[0.95] mb-6" data-testid="org-hero-title">
              Build cultural fluency<br />across your team
            </h1>
            <p className="font-sans text-base sm:text-lg text-cream/40 font-light leading-[1.8] max-w-xl mb-8">
              Workshops and advisory services that give multicultural teams a shared language for navigating difference — with clarity, consent, and practical outcomes.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#enquire" className="border border-gold text-gold px-7 py-3.5 text-sm uppercase tracking-[0.12em] font-sans font-medium inline-flex items-center gap-2.5 hover:bg-gold hover:text-navy transition-all duration-300">
                Enquire <ArrowRight size={15} />
              </a>
              <Link to="/cfq-assessment" className="border border-cream/15 text-cream/50 px-7 py-3.5 text-sm uppercase tracking-[0.12em] font-sans font-medium inline-flex items-center gap-2.5 hover:border-gold/40 hover:text-gold transition-all duration-300">
                Try the CFQ
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/15 to-transparent" />
      </section>

      {/* How it works */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <span className="block text-[11px] tracking-[0.3em] uppercase text-gold/60 font-sans font-medium mb-4">How It Works</span>
          <h2 className="font-serif text-3xl sm:text-4xl text-cream font-light tracking-tight leading-[1.1] mb-4 max-w-lg">
            The CFQ in your organisation
          </h2>
          <p className="font-sans text-base text-cream/35 font-light leading-[1.8] mb-14 max-w-xl">
            The Cultural Fluency Quotient is a 20-question reflective tool across four pillars: Self-Anchoring, Perspective-Shifting, Adaptive Communication, and Relational Integration. In organisational settings, it is always facilitated.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { step: '01', title: 'Discovery call', desc: 'We discuss your team, context, and goals. No generic packages — every engagement is tailored.' },
              { step: '02', title: 'Consent & preparation', desc: 'Participants receive clear information about the assessment. Consent is gathered before any data is collected.' },
              { step: '03', title: 'Facilitated session', desc: 'The CFQ is completed and discussed in a guided workshop. Results stay with the individual unless they choose to share.' },
              { step: '04', title: 'Individual development goals', desc: 'Each participant identifies one observable behaviour to work on — practical, specific, and owned by them.' },
              { step: '05', title: 'Team insights (opt-in)', desc: 'With consent, anonymised themes can be shared to help the team build shared awareness — never individual comparison.' },
              { step: '06', title: 'Follow-up', desc: 'Optional quarterly check-ins to review progress, adjust goals, and deepen the team\'s cultural fluency over time.' },
            ].map((item) => (
              <div key={item.step} className="border border-cream/[0.06] bg-white/[0.015] p-7 hover:border-gold/20 transition-all">
                <span className="font-serif text-2xl text-gold/20 font-light block mb-3">{item.step}</span>
                <h3 className="font-sans text-base text-cream/70 font-medium mb-2">{item.title}</h3>
                <p className="font-sans text-sm text-cream/30 font-light leading-[1.8]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Offerings */}
      <section className="py-20 md:py-24 relative">
        <div className="absolute inset-0 bg-white/[0.01]" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <span className="block text-[11px] tracking-[0.3em] uppercase text-gold/60 font-sans font-medium mb-4">What We Offer</span>
          <h2 className="font-serif text-3xl sm:text-4xl text-cream font-light tracking-tight leading-[1.1] mb-14">Formats</h2>

          <div className="grid md:grid-cols-2 gap-4">
            {offerings.map((item) => (
              <div key={item.title} className="border border-cream/[0.06] bg-white/[0.015] p-8 md:p-10 hover:border-gold/20 transition-all group">
                <item.Icon size={20} className="text-gold/40 mb-5 group-hover:text-gold transition-colors" />
                <h3 className="font-serif text-xl text-cream font-light tracking-tight mb-1">{item.title}</h3>
                <span className="text-[11px] uppercase tracking-[0.2em] text-gold/40 font-sans block mb-4">{item.duration}</span>
                <p className="font-sans text-base text-cream/35 font-light leading-[1.8]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <span className="block text-[11px] tracking-[0.3em] uppercase text-gold/60 font-sans font-medium mb-4">Our Principles</span>
          <h2 className="font-serif text-3xl sm:text-4xl text-cream font-light tracking-tight leading-[1.1] mb-4 max-w-lg">
            How we handle data and consent
          </h2>
          <p className="font-sans text-base text-cream/35 font-light leading-[1.8] mb-14 max-w-xl">
            The CFQ is a developmental mirror — not a performance metric. These principles are non-negotiable in every engagement.
          </p>

          <div className="grid sm:grid-cols-2 gap-4">
            {principles.map((item) => (
              <div key={item.title} className="border-l-2 border-gold/20 pl-6 py-2">
                <div className="flex items-center gap-2 mb-2">
                  <item.Icon size={16} className="text-gold/50" />
                  <h4 className="font-sans text-base text-cream/60 font-medium">{item.title}</h4>
                </div>
                <p className="font-sans text-base text-cream/30 font-light leading-[1.8]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enquiry form */}
      <section className="py-20 md:py-24 relative" id="enquire">
        <div className="absolute inset-0 bg-gradient-to-b from-gold/[0.02] to-transparent pointer-events-none" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-12">
          <span className="block text-[11px] tracking-[0.3em] uppercase text-gold/60 font-sans font-medium mb-4">Get In Touch</span>
          <h2 className="font-serif text-3xl sm:text-4xl text-cream font-light tracking-tight leading-[1.1] mb-4">
            Enquire about workshops
          </h2>
          <p className="font-sans text-base text-cream/35 font-light leading-[1.8] mb-10">
            Tell us about your team and we will respond within two business days with a tailored proposal.
          </p>

          {status === 'success' ? (
            <div className="border border-green-500/20 bg-green-500/[0.03] p-10 text-center" data-testid="enquiry-success">
              <CheckCircle size={32} className="text-green-400 mx-auto mb-4" />
              <h3 className="font-serif text-2xl text-cream font-light mb-2">Enquiry sent</h3>
              <p className="text-cream/30 text-base mb-6">We will be in touch within two business days.</p>
              <button onClick={() => { setStatus('idle'); setForm({ name: '', email: '', organisation: '', role: '', team_size: '', message: '' }); }} className="text-gold text-sm uppercase tracking-[0.12em] hover:text-gold-light">
                Send another <ArrowRight size={12} className="inline ml-1" />
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="enquiry-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <input name="name" value={form.name} onChange={handleChange} required placeholder="Your name" className={inputClass} data-testid="org-name-input" />
                <input name="email" type="email" value={form.email} onChange={handleChange} required placeholder="Work email" className={inputClass} data-testid="org-email-input" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input name="organisation" value={form.organisation} onChange={handleChange} required placeholder="Organisation" className={inputClass} data-testid="org-org-input" />
                <input name="role" value={form.role} onChange={handleChange} placeholder="Your role" className={inputClass} data-testid="org-role-input" />
              </div>
              <select name="team_size" value={form.team_size} onChange={handleChange} className={inputClass} style={{ colorScheme: 'dark' }} data-testid="org-size-select">
                <option value="">Team size</option>
                <option value="5-15">5–15 people</option>
                <option value="16-50">16–50 people</option>
                <option value="51-200">51–200 people</option>
                <option value="200+">200+ people</option>
              </select>
              <textarea name="message" value={form.message} onChange={handleChange} required rows={4} placeholder="Tell us about your team, context, and what you are hoping to achieve..." className={`${inputClass} resize-none`} data-testid="org-message-input" />

              {status === 'error' && (
                <div className="flex items-center gap-2 text-red-400 text-sm">
                  <AlertCircle size={14} /> {errorMsg || 'Something went wrong. Please try again.'}
                </div>
              )}

              <button type="submit" disabled={status === 'loading'} className="border border-gold text-gold px-8 py-4 text-sm uppercase tracking-[0.12em] font-sans font-medium inline-flex items-center gap-2.5 hover:bg-gold hover:text-navy transition-all duration-300 disabled:opacity-50" data-testid="org-submit-btn">
                {status === 'loading' ? <><Loader2 size={15} className="animate-spin" /> Sending...</> : <><Send size={15} /> Send Enquiry</>}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 border-t border-cream/[0.04]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <p className="font-sans text-base text-cream/30 font-light mb-4">Want to try the assessment yourself first?</p>
          <Link to="/cfq-assessment" className="text-sm uppercase tracking-[0.12em] text-gold/50 font-sans hover:text-gold transition-colors inline-flex items-center gap-2">
            Take the individual CFQ <ArrowRight size={13} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Organisations;
